import { useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import {
  ApiDownloadButton,
  Badge,
  ContentLabel,
  ContentValue,
  DisplayFormattedDate,
  UserPathStepProgress
} from 'components/common';
import { Box } from 'components/common/box';

import type { Path, PathStepProgress, User } from 'shared-types';

interface Props {
  isUserPage?: boolean;
  loading: boolean;
  path: Pick<Path, 'certificateAllowDownloading' | 'id' | 'title'> & {
    owners: Pick<User, 'id' | 'firstName' | 'lastName' | 'username'>[];
    progressData: PathStepProgress;
  };
}

const ProfilePathsPathProgress = ({ isUserPage, loading, path }: Props) => {
  const { language, messages, theme } = useReduxState();

  const valueStyle = {
    color: theme.textColorLighter,
    fontSize: '1.5rem',
    fontWeight: 600
  };

  const completionHasBeenUpdated =
    path?.progressData?.pathCompletion &&
    getFormattedDate({ date: path.progressData.pathCompletion.createdAt }) !==
      getFormattedDate({
        date: path.progressData.pathCompletion.completionDate
      });

  return (
    <div className="progress">
      <Box loading={loading} padding={20} title={messages.commonProgress}>
        {path ? (
          <>
            <ContentLabel
              horizontal={true}
              label={messages.commonStatus}
              spacing={20}
            >
              <ContentValue>
                {path.progressData?.pathCompletion ? (
                  <Badge
                    category="success-light"
                    isRounded={false}
                    text={messages.commonCompleted}
                  />
                ) : (
                  <Badge
                    category="default"
                    isRounded={false}
                    text={
                      path.progressData?.stepsStarted
                        ? messages.commonInProgress
                        : messages.commonNotStarted
                    }
                  />
                )}
              </ContentValue>
            </ContentLabel>

            <ContentLabel
              horizontal={true}
              label={messages.commonProgress}
              spacing={20}
            >
              <ContentValue>
                <UserPathStepProgress
                  progressData={path.progressData}
                  progressStyle={{ marginTop: 7 }}
                />
              </ContentValue>
            </ContentLabel>

            {completionHasBeenUpdated ? (
              <>
                <ContentLabel
                  horizontal={true}
                  label={messages.completionsCompletionDateOriginal}
                  spacing={20}
                >
                  <ContentValue style={valueStyle}>
                    <DisplayFormattedDate
                      date={path.progressData.pathCompletion.createdAt}
                    />
                  </ContentValue>
                </ContentLabel>

                <ContentLabel
                  horizontal={true}
                  label={messages.completionsCompletionDateUpdated}
                  spacing={20}
                >
                  <ContentValue style={valueStyle}>
                    <DisplayFormattedDate
                      date={path.progressData.pathCompletion.completionDate}
                    />
                  </ContentValue>
                </ContentLabel>
              </>
            ) : path.progressData?.pathCompletion?.completionDate ? (
              <ContentLabel
                horizontal={true}
                label={messages.completionsCompletionDate}
                spacing={20}
              >
                <ContentValue style={valueStyle}>
                  <DisplayFormattedDate
                    date={path.progressData.pathCompletion.completionDate}
                  />
                </ContentValue>
              </ContentLabel>
            ) : null}

            {path &&
            (isUserPage || path.certificateAllowDownloading) &&
            path.progressData?.pathCompletion ? (
              <ContentLabel
                horizontal={true}
                label={messages.commonCertificate}
                style={{ whiteSpace: 'nowrap' }}
              >
                <ApiDownloadButton
                  category="link-with-background"
                  filename={`${path.title}.pdf`}
                  icon={['far', 'file-pdf']}
                  iconStyle={{
                    color: '#F40F02',
                    fontSize: '1.4rem',
                    top: 0
                  }}
                  size="header"
                  text={messages.completionsDownloadCertificate}
                  url={
                    `/admin/path-certificate` +
                    `?completionId=${path.progressData.pathCompletion.id}` +
                    `&language=${language}`
                  }
                />
              </ContentLabel>
            ) : null}
          </>
        ) : null}
      </Box>
    </div>
  );
};

export default ProfilePathsPathProgress;
